import { type ClientResponse, NDT7TestCore } from '@soluto-private/ndt-javascript';

import type { SpeedTestEmitterI, SpeedTestToolI } from '../types';
import { SpeedTestEmitter } from './SpeedTestEmitter';
import { SpeedTestEmitterKey, SpeedTestStatus, SpeedtestType } from '../enums';

// This class implements the NDT7 speed test tool, extending the core functionality
// provided by the NDT7TestCore class. It handles the speed test logic, including
// starting the test, processing results, and managing the status of the speed test.
// It emits events for status changes and speed data updates, allowing other parts
// of the application to react to these changes.
export class Ndt7 extends NDT7TestCore implements SpeedTestToolI {
	private speedTestTypeToRun: SpeedtestType;
	private _emitter: SpeedTestEmitterI;

	constructor(speedTestTypeToRun: SpeedtestType) {
		super();
		this.speedTestTypeToRun = speedTestTypeToRun;
		this._emitter = new SpeedTestEmitter();
	}

	public get emitter(): SpeedTestEmitterI {
		return this._emitter;
	}

	public async scan() {
		try {
			await this.startTest(this.speedTestTypeToRun, {
				onErrorCallback(eventName, extraInfo) {
					console.error(
						`Error in NDT7 test: ${eventName}`,
						extraInfo ? `Extra Info: ${JSON.stringify(extraInfo)}` : ''
					);
				},
			});
		} catch (error) {
			console.error('Error running speed test:', error);
		}
	}

	public cancel(): void {
		this.cancelNDT7Test();
		this.handleSetStatus(SpeedTestStatus.canceled);
	}

	override onDownloadSpeedAppNDT7Data(data: ClientResponse): void {
		this.handleSetSpeedData(SpeedtestType.DOWNLOAD, `${data.ClientData.MeanClientMbps}`);
	}

	override onUploadSpeedAppNDT7Data(data: ClientResponse): void {
		this.handleSetSpeedData(SpeedtestType.UPLOAD, `${data.ClientData.MeanClientMbps}`);
	}

	override onTestStarted(testType: SpeedtestType): void {
		const speedTestStatus =
			testType === SpeedtestType.UPLOAD
				? SpeedTestStatus.inProgressUpload
				: SpeedTestStatus.inProgressDownload;

		this.handleSetStatus(speedTestStatus);
	}

	override onFinished(testType: SpeedtestType): void {
		switch (this.speedTestTypeToRun) {
			case SpeedtestType.DOWNLOAD:
				if (testType === SpeedtestType.DOWNLOAD) this.testComplete();
				return;
			case SpeedtestType.UPLOAD:
				if (testType === SpeedtestType.UPLOAD) this.testComplete();
				return;
			case SpeedtestType.UPLOAD_AND_DOWNLOAD:
				if (testType === SpeedtestType.UPLOAD) this.testComplete();
				return;
		}
	}

	private testComplete() {
		this.handleSetStatus(SpeedTestStatus.completed);
	}

	private handleSetStatus(status: SpeedTestStatus) {
		this._emitter?.emit(SpeedTestEmitterKey.STATUS, status);
	}

	private handleSetSpeedData(speedtestType: SpeedtestType, speed: string) {
		if (speedtestType === SpeedtestType.DOWNLOAD) {
			this._emitter?.emit(SpeedTestEmitterKey.DOWNLOAD_SPEED, speed);
		} else {
			this._emitter?.emit(SpeedTestEmitterKey.UPLOAD_SPEED, speed);
		}
	}
}
